'use strict';

Object.defineProperty(exports, "__esModule", {
  value: true
});

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

// NO FILE EXTENSION, to work around JSPM bug in handling package.json's "browser" field
// see https://github.com/jspm/jspm-cli/issues/1062#issuecomment-170342414


exports.load = load;
exports.read = read;

var _Coverage = require('./Coverage.js');

var _Coverage2 = _interopRequireDefault(_Coverage);

var _CoverageCollection = require('./CoverageCollection.js');

var _CoverageCollection2 = _interopRequireDefault(_CoverageCollection);

var _util = require('./util.js');

var _constants = require('./constants.js');

var _http = require('./http');

var http = _interopRequireWildcard(_http);

function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } else { var newObj = {}; if (obj != null) { for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) newObj[key] = obj[key]; } } newObj.default = obj; return newObj; } }

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

/**
 * Loads a CoverageJSON document from a given URL and returns a {@link Promise} object
 * that succeeds with the unmodified CoverageJSON object.
 * 
 * @param {string} url The URL to load the CoverageJSON document from.
 * @param {Object} [options] An options object. 
 * @param {Object} [options.headers] Additional HTTP headers to send if input is a URL.
 * @param {Object} [options.eagerload]
 *   Request a stand-alone CoverageJSON document (with domain and ranges embedded) if input is a URL.
 *   Note that the server may ignore that preference.
 * @return {Promise}
 *   A Promise succeeding with an object <code>{data, headers}</code> where data is the CoverageJSON object
 *   and headers are the HTTP response headers with lower-cased header names as object keys.
 *   The promise fails if the resource at the given URL is not a valid JSON or CBOR document. 
 */
function load(url, options) {
  return http.load(url, options);
}

/**
 * Reads a CoverageJSON document and returns a {@link Promise} that succeeds with
 * a Domain, {@link Coverage}, or {@link CoverageCollection} object.
 * 
 * Note that if the document references external domain or range documents,
 * then these are not loaded immediately. 
 * 
 * 
 * @example
 * CovJSON.read('http://example.com/coverage.covjson').then(function (cov) {
 *   // work with Coverage data object
 * }).catch(function (e) {
 *   // there was an error when loading the coverage data
 *   console.log(e)
 * })
 * @param {Object|string} input 
 *    A CoverageJSON Domain, Coverage, or Coverage Collection document, as URL or object.
 * @param {Object} [options]
 *   An options object. 
 * @param {Object} [options.headers]
 *   Additional HTTP headers to send if input is a URL.
 * @param {Object} [options.eagerload]
 *   Request a stand-alone CoverageJSON document (with domain and ranges embedded) if input is a URL.
 *   Note that the server may ignore that preference. 
 * @return {Promise} 
 *    A promise object succeeding with a Domain, {@link Coverage}, or {@link CoverageCollection} object,
 *    and failing with an {@link Error} object.
 */
function read(input) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  if ((typeof input === 'undefined' ? 'undefined' : _typeof(input)) === 'object') {
    return Promise.resolve().then(function () {
      return transformCovJSON(input);
    });
  } else {
    return load(input, options).then(function (_ref) {
      var data = _ref.data;
      var headers = _ref.headers;
      return transformCovJSON(data, headers);
    });
  }
}

/**
 * Transforms a CoverageJSON object into one or more Coverage objects.
 *  
 * @param {object} obj A CoverageJSON object of type Coverage or CoverageCollection.
 * @param {array} headers An optional array of HTTP headers. Keys are lower-cased header names.
 * @return {Coverage|Array of Coverage}
 */
function transformCovJSON(obj, headers) {
  checkValidCovJSON(obj);
  if ([_constants.COVERAGE, _constants.COVERAGECOLLECTION, _constants.DOMAIN].indexOf(obj.type) === -1) {
    throw new Error('CoverageJSON document must be of Coverage, CoverageCollection, or Domain type');
  }

  var result = void 0;
  if (obj.type === _constants.DOMAIN) {
    (0, _Coverage.transformDomain)(obj);
    result = obj;
  } else if (obj.type === _constants.COVERAGE) {
    result = new _Coverage2.default(obj);
  } else {
    result = new _CoverageCollection2.default(obj);
  }

  if (obj.type === _constants.COVERAGE || obj.type === _constants.COVERAGECOLLECTION) {
    addLinkRelations(result, headers);
  }

  return result;
}

/**
 * Scans the supplied HTTP headers for Link relations and adds them
 * to the .ld property of the Coverage/CoverageCollection.
 */
function addLinkRelations(cov, headers) {
  if (!headers || !headers['link']) {
    return;
  }

  var ld = cov.ld;

  var _iteratorNormalCompletion = true;
  var _didIteratorError = false;
  var _iteratorError = undefined;

  try {
    for (var _iterator = headers['link'].split(',')[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
      var link = _step.value;

      link = link.trim();
      // FIXME this will fail if the URL contains a ";" which is valid (see RFC5988)
      var parts = link.split(';');
      var url = parts[0].substr(1, parts[0].length - 2);
      var _iteratorNormalCompletion2 = true;
      var _didIteratorError2 = false;
      var _iteratorError2 = undefined;

      try {
        for (var _iterator2 = parts.slice(1)[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
          var param = _step2.value;

          var relStart = param.indexOf('rel=');
          if (relStart === -1) {
            continue;
          }
          var rel = param.substring(relStart + 5, param.length - 1);
          if (rel.indexOf('http://') !== 0 && rel.indexOf('https://') !== 0) {
            rel = _constants.LINKRELPREFIX + rel;
          }
          if (ld[rel]) {
            if (Array.isArray(ld[rel])) {
              ld[rel].push(url);
            } else {
              ld[rel] = [ld[rel], url];
            }
          } else {
            ld[rel] = url;
          }
        }
      } catch (err) {
        _didIteratorError2 = true;
        _iteratorError2 = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion2 && _iterator2.return) {
            _iterator2.return();
          }
        } finally {
          if (_didIteratorError2) {
            throw _iteratorError2;
          }
        }
      }
    }
  } catch (err) {
    _didIteratorError = true;
    _iteratorError = err;
  } finally {
    try {
      if (!_iteratorNormalCompletion && _iterator.return) {
        _iterator.return();
      }
    } finally {
      if (_didIteratorError) {
        throw _iteratorError;
      }
    }
  }
}

/**
 * Performs basic structural checks to validate whether a given object is a CoverageJSON object.
 * 
 * Note that this method is not comprehensive and should not be used for checking
 * whether an object fully conforms to the CoverageJSON specification.
 * 
 * @param obj
 * @throws {Error} when obj is not a valid CoverageJSON document 
 */
function checkValidCovJSON(obj) {
  (0, _util.assert)('type' in obj, '"type" missing');
  if (obj.type === _constants.COVERAGE) {
    (0, _util.assert)('parameters' in obj, '"parameters" missing');
    (0, _util.assert)('domain' in obj, '"domain" missing');
    (0, _util.assert)('ranges' in obj, '"ranges" missing');
  } else if (obj.type === _constants.COVERAGECOLLECTION) {
    (0, _util.assert)(Array.isArray(obj.coverages), '"coverages" must be an array');
  }
}